/**
 * Ohm's Law calculator: V = I × R
 * Accepts engineering suffixes: 12V, 20mA, 10kΩ, 1uA, etc.
 */

import React, { useState, useMemo } from 'react';
import { parseEngineering, inputClass, resultClass, labelClass, hintClass } from './parseEngineering';

const ts = () => new Date().toISOString();

const OhmsLaw = () => {
  const [voltage, setVoltage] = useState('');
  const [current, setCurrent] = useState('');
  const [resistance, setResistance] = useState('');

  console.log(`[${ts()}] [OhmsLaw] render`);

  const v = useMemo(() => parseEngineering(voltage), [voltage]);
  const i = useMemo(() => parseEngineering(current), [current]);
  const r = useMemo(() => parseEngineering(resistance), [resistance]);

  const result = useMemo(() => {
    if (v != null && v >= 0 && i != null && i >= 0 && r == null) return { type: 'R', value: v / i, unit: 'Ω' };
    if (v != null && v >= 0 && i == null && r != null && r > 0) return { type: 'I', value: v / r, unit: 'A' };
    if (v == null && i != null && i >= 0 && r != null && r >= 0) return { type: 'V', value: i * r, unit: 'V' };
    return null;
  }, [v, i, r]);

  return (
    <div className="space-y-4">
      <p className={hintClass}>
        Enter any two of V, I, R. Use suffixes: 12V, 20mA, 10kΩ, 1uA, 2.2k, etc.
      </p>
      <p className="text-gray-400 text-xs">
        Try: 12V and 20mA → 600 Ω
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className={labelClass}>Voltage V</label>
          <input
            type="text"
            inputMode="decimal"
            value={voltage}
            onChange={(e) => setVoltage(e.target.value)}
            placeholder="e.g. 12 or 12V"
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass}>Current I</label>
          <input
            type="text"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            placeholder="e.g. 20mA or 0.02"
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass}>Resistance R</label>
          <input
            type="text"
            value={resistance}
            onChange={(e) => setResistance(e.target.value)}
            placeholder="e.g. 470 or 10k"
            className={inputClass}
          />
        </div>
      </div>
      {result && (
        <div className={resultClass}>
          <span className="text-gray-600 text-sm">Result: </span>
          <span className="font-semibold text-gray-900">
            {result.type} = {result.value < 1e-3 || result.value >= 1e6 ? result.value.toExponential(4) : result.value.toFixed(4)} {result.unit}
          </span>
        </div>
      )}
    </div>
  );
};

export default OhmsLaw;
